import React, { useState } from 'react';
import { 
  FlatList, 
  View, 
  Text, 
  StyleSheet, 
  RefreshControl,
  ActivityIndicator,
  Alert 
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../hooks/useColors';
import { spacing, typography } from '../theme';
import { useRealNotifications as useNotifications } from '../hooks/useRealNotifications';
import { Notification } from '../store/slices/notificationsSlice';
import { NotificationCard } from './NotificationCard';

interface NotificationListProps {
  filterType?: Notification['type'];
  showHeader?: boolean;
  maxHeight?: number;
  customFilter?: (notifications: Notification[]) => Notification[];
}

export function NotificationList({ 
  filterType, 
  showHeader = true, 
  maxHeight,
  customFilter 
}: NotificationListProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const [refreshing, setRefreshing] = useState(false);
  
  const { 
    notifications, 
    loading, 
    error, 
    fetchNotifications, 
    markAsRead, 
    deleteNotification 
  } = useNotifications();

  // Filter notifications if filterType or customFilter is provided
  const filteredNotifications = customFilter
    ? customFilter(notifications)
    : filterType 
    ? notifications.filter(n => n.type === filterType)
    : notifications;

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchNotifications();
    setRefreshing(false);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleDelete = (notification: Notification) => {
    Alert.alert(
      'Delete Notification',
      'Are you sure you want to delete this notification?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: () => deleteNotification(notification.id)
        }
      ]
    );
  };

  const renderNotification = ({ item }: { item: Notification }) => (
    <NotificationCard
      notification={item}
      onMarkAsRead={() => handleMarkAsRead(item.id)}
      onDelete={() => handleDelete(item)}
    />
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <View style={styles.emptyIconContainer}>
        <Ionicons name="notifications-off-outline" size={64} color="#FFFFFF" />
      </View>
      <Text style={styles.emptyTitle}>No Notifications</Text>
      <Text style={styles.emptyText}>
        {filterType 
          ? `You don't have any ${filterType.replace('_', ' ')} notifications yet.`
          : 'You\'re all caught up! When you get new notifications, they will appear here.'
        }
      </Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <Ionicons name="alert-circle-outline" size={48} color={colors.error} />
      <Text style={styles.errorTitle}>Something went wrong</Text>
      <Text style={styles.errorText}>
        {error?.message || 'We couldn\'t load your notifications. Please try again.'}
      </Text>
    </View>
  );

  const renderHeader = () => {
    if (!showHeader) return null;
    
    const unreadCount = filteredNotifications.filter(n => n.status === 'unread').length;
    
    return (
      <View style={styles.header}>
        <Text style={styles.headerTitle}>
          {filterType ? filterType.replace('_', ' ').toUpperCase() : 'Notifications'}
        </Text>
        {unreadCount > 0 && (
          <Text style={styles.unreadCount}>
            {unreadCount} unread
          </Text>
        )}
      </View>
    );
  };

  if (loading && !refreshing && filteredNotifications.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Loading notifications...</Text>
      </View>
    );
  }

  if (error && filteredNotifications.length === 0) {
    return renderError();
  }

  return (
    <View style={[styles.container, maxHeight && { maxHeight }]}>
      <FlatList
        data={filteredNotifications}
        renderItem={renderNotification}
        keyExtractor={(item, index) => `${item.id}-${index}`}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={renderEmpty}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
        contentContainerStyle={[
          styles.listContent,
          filteredNotifications.length === 0 && styles.emptyListContent
        ]}
        showsVerticalScrollIndicator={false}
        ItemSeparatorComponent={null}
      />
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  listContent: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xs,
    paddingBottom: spacing.xl,
  },
  emptyListContent: {
    flex: 1,
    justifyContent: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    ...typography.subtitle,
    color: colors.text,
    fontWeight: '600',
  },
  unreadCount: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '600',
  },
  separator: {
    height: spacing.xs,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    marginTop: 60,
  },
  emptyIconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: "#000000",
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  emptyTitle: {
    ...typography.subtitle,
    color: colors.text,
    marginBottom: spacing.xs,
    textAlign: 'center',
    fontWeight: '700',
  },
  emptyText: {
    ...typography.body,
    color: colors.textLight,
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: spacing.md,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    marginTop: 60,
  },
  errorTitle: {
    ...typography.subtitle,
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
    textAlign: 'center',
    fontWeight: '700',
  },
  errorText: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.md,
  },
});